import {
  captureException,
  getCurrentHub,
  startTransaction,
} from '@sentry/browser';
import { Transaction } from '@sentry/tracing';
import { Barcode } from './barcode';
import { BarcodePreviewHandler } from './barcode-preview-handler';
import { FormHandler } from './form-handler';
import { LabelPreviewHandler } from './label-preview-handler';
import { PdfRenderer } from './pdf-renderer';
import { ProgressBarHandler } from './progress-bar-handler';
import { TemplateInputHandler } from './template-input-handler';
import { plausible } from './utils/plausible.fn';
import { range } from './utils/range.fn';

export class Application {
  private formHandler = new FormHandler('body form');
  private progressBar = new ProgressBarHandler('.loading-wrapper', 0);
  private barcodePreview = new BarcodePreviewHandler('#barcode-preview');
  private pagePreview = new LabelPreviewHandler('#page-preview svg');
  private barcodeTemplatePrefix = new TemplateInputHandler(
    '#barcode-template-prefix',
  );
  private barcodeTemplateSuffix = new TemplateInputHandler(
    '#barcode-template-suffix',
  );

  async run() {
    this.formHandler.onSubmit(e => this.handleSubmit(e));
    this.formHandler.subscribeToChanges(
      e => this.onBarcodeSetupChange(),
      'barcodeType',
      'rangeStart',
      'rangeEnd',
      'barcodeTemplatePrefix',
      'barcodeTemplateSuffix',
      'additionalText',
    );
    this.formHandler.subscribeToChanges(
      e => this.onLabelSetupChange(),
      'paperTopMargin',
      'paperLeftMargin',
      'paperBottomMargin',
      'paperRightMargin',
      'paperType',
      'labelGutterHeight',
      'labelGutterWidth',
      'labelHeight',
      'labelWidth',
      'numberOfColumns',
    );
    this.barcodeTemplatePrefix.observe();
    this.barcodeTemplateSuffix.observe();

    await this.onBarcodeSetupChange();
    await this.onLabelSetupChange();
  }

  private async onBarcodeSetupChange() {
    const {
      barcodeType,
      rangeStart,
      additionalText,
      barcodeTemplatePrefix,
      barcodeTemplateSuffix,
    } = this.formHandler.getValues();

    await this.barcodePreview.renderPreview(
      barcodeType,
      rangeStart,
      additionalText,
      barcodeTemplatePrefix,
      barcodeTemplateSuffix,
    );
  }

  private async onLabelSetupChange() {
    const {
      paperTopMargin,
      paperLeftMargin,
      paperBottomMargin,
      paperRightMargin,
      paperType,
      labelGutterHeight,
      labelGutterWidth,
      labelHeight,
      labelWidth,
      numberOfColumns,
    } = this.formHandler.getValues();

    await this.pagePreview.renderPreview(
      paperType,
      paperLeftMargin,
      paperTopMargin,
      paperRightMargin,
      paperBottomMargin,
      labelGutterHeight,
      labelGutterWidth,
      labelHeight,
      labelWidth,
      numberOfColumns,
    );
  }

  private async handleSubmit(e: SubmitEvent) {
    const formValues = this.formHandler.getValues();

    try {
      let transaction: Transaction | undefined;
      try {
        transaction = startTransaction({
          name: 'Generate Barcode',
          op: 'handleSubmit',
          data: formValues?.toObject(),
        }) as Transaction;
        getCurrentHub()?.configureScope(scope => scope.setSpan(transaction));
      } catch (e) {
        console.error(e);
      }

      await this.formHandler.hide();
      await this.progressBar.show();

      plausible('Generated Barcodes', { props: formValues });

      this.progressBar.setMaxValue(formValues.rangeEnd * 3);

      const barcodes = [] as Barcode[];

      const generateBarcodeObjectsSpan = transaction?.startChild({
        op: 'generate-barcode-objects',
        description: 'generate barcode objects',
      });
      for (const i of range(formValues.rangeStart, formValues.rangeEnd)) {
        try {
          const value = `${formValues.barcodeTemplatePrefix}${i}${formValues.barcodeTemplateSuffix}`;
          const barcode = new Barcode(value, formValues.additionalText);
          barcodes.push(barcode);

          this.progressBar.increment();
        } catch (e) {
          console.error(e);
        }
      }
      generateBarcodeObjectsSpan?.finish();

      await new Promise<void>(resolve => setTimeout(() => resolve(), 100));

      this.progressBar.setInfo('Rendering barcodes... 🪛');

      let noOfRenderedBarcodes = 0;
      const renderBarcodesSpan = transaction?.startChild({
        op: 'render-barcodes',
        description: 'render barcodes',
      });
      await Promise.all(
        barcodes.map(b =>
          b
            .render(
              formValues.barcodeType,
              formValues.labelHeight,
              formValues.labelWidth,
              formValues.renderingQuality,
            )
            .then(() => {
              noOfRenderedBarcodes++;

              requestAnimationFrame(() => {
                this.progressBar.setInfo(
                  `Rendering barcodes... ${noOfRenderedBarcodes} of ${barcodes.length} 🪛`,
                );
                this.progressBar.increment();
              });
            }),
        ),
      );
      renderBarcodesSpan?.finish();
      await new Promise<void>(resolve => setTimeout(() => resolve(), 100));
      this.progressBar.setInfo('Constructing PDF document... 📄');

      const constructPdfSpan = transaction?.startChild({
        op: 'construct-pdf',
        description: 'construct pdf',
      });
      const pdfRenderer = new PdfRenderer(barcodes);

      await pdfRenderer.render(
        formValues,
        () => this.progressBar.increment(),
        ({ page, total }) =>
          this.progressBar.setInfo(
            `Constructing PDF document... Page ${page + 1} of ${total} 📄`,
          ),
        () =>
          this.progressBar.setInfo('Finished constructing PDF document... 📄'),
      );
      constructPdfSpan?.finish();

      await new Promise<void>(resolve => setTimeout(() => resolve(), 2500));

      await this.progressBar.hide();
      await this.progressBar.update(0);
      await this.formHandler.show();

      transaction?.finish();
    } catch (e) {
      try {
        captureException(e);
        console.error(e);
      } catch (e1) {
        console.error(e1);
      }
    }
  }
}
